import { levenshteinDistance } from "../maths/maths";
import type { CategoryType, ItemCategory } from "../../types";

const NAME_SUBSTRING_SCORE = 10;
const DESCRIPTION_SUBSTRING_SCORE = 5;
const CATEGORY_SUBSTRING_SCORE = 3;

const NAME_TOKEN_WEIGHT = 1;
const DESCRIPTION_TOKEN_WEIGHT = 0.2;
const CATEGORY_TOKEN_WEIGHT = 0.2;

const SCORE_THRESHOLD = 1;

const tokenize = (text: string): string[] =>
  text
    .toLowerCase()
    .split(/\s+/)
    .filter((token) => token.length > 0);

const fuzzyScore = (
  queryTokens: string[],
  fieldTokens: string[],
  weight: number
): number => {
  let score = 0;

  for (const queryToken of queryTokens) {
    for (const fieldToken of fieldTokens) {
      if (levenshteinDistance(queryToken, fieldToken) < fieldToken.length / 2) {
        score += weight;
      }
    }
  }

  return score;
};

const joinLocalized = (value?: string, localized?: string): string =>
  localized
    ? `${value ?? ""} ${localized}`.toLowerCase()
    : (value ?? "").toLowerCase();

type RankedItem = { itemCategory: ItemCategory; score: number };

export const rankMenuItems = (
  categories: CategoryType[] | null,
  query: string
): RankedItem[] => {
  const trimmedQuery = query.trim().toLowerCase();

  if (!trimmedQuery || !categories?.length) {
    return [];
  }

  const queryTokens = tokenize(trimmedQuery);
  const ranked: RankedItem[] = [];
  const indexById = new Map<number, number>();
  let menuIndex = 0;
  const orderById = new Map<number, number>();

  for (const category of categories) {
    const categoryText = joinLocalized(
      category.category_name,
      category.nameLocalized
    );
    const categoryTokens = tokenize(categoryText);

    let categoryScore = categoryText.includes(trimmedQuery)
      ? CATEGORY_SUBSTRING_SCORE
      : 0;
    categoryScore += fuzzyScore(
      queryTokens,
      categoryTokens,
      CATEGORY_TOKEN_WEIGHT
    );

    for (const itemCategory of category.itemCategories) {
      const { item } = itemCategory;

      if (!orderById.has(item.id)) {
        orderById.set(item.id, menuIndex++);
      }

      const existingIndex = indexById.get(item.id);

      if (existingIndex !== undefined) {
        ranked[existingIndex].score += categoryScore;
        continue;
      }

      const nameText = joinLocalized(item.item_name, item.nameLocalized);
      const descriptionText = joinLocalized(
        item.description,
        item.descriptionLocalized
      );

      let score = nameText.includes(trimmedQuery) ? NAME_SUBSTRING_SCORE : 0;
      score += fuzzyScore(queryTokens, tokenize(nameText), NAME_TOKEN_WEIGHT);

      if (descriptionText.includes(trimmedQuery)) {
        score += DESCRIPTION_SUBSTRING_SCORE;
      }
      score += fuzzyScore(
        queryTokens,
        tokenize(descriptionText),
        DESCRIPTION_TOKEN_WEIGHT
      );

      score += categoryScore;

      if (score > 0) {
        indexById.set(item.id, ranked.length);
        ranked.push({ itemCategory, score });
      }
    }
  }

  ranked.sort(
    (a, b) =>
      b.score - a.score ||
      (orderById.get(a.itemCategory.item.id) ?? 0) -
        (orderById.get(b.itemCategory.item.id) ?? 0)
  );

  const threshold = ranked.length
    ? Math.min(SCORE_THRESHOLD, ranked[0].score)
    : SCORE_THRESHOLD;

  return ranked.filter((result) => result.score >= threshold);
};
